/**
 * VersionDisplay Component
 *
 * Displays the application version with environment-specific build numbers.
 *
 * Display Format:
 * - Development: Mercury TNA v{version}-dev.{n}
 * - Staging: Mercury TNA v{version}-stg.{n}
 * - Production: Mercury TNA v{version}
 *
 * @see docs/03-Lifecycle/ChangeMgmt/VC-TNA-2026-001-Version_Control_Policy.md
 */

import versionConfig from "../../config/version.json"

/**
 * Get the display version string based on current environment
 * @returns {string} Formatted version string
 */
export const getDisplayVersion = () => {
  const env = process.env.REACT_APP_ENVIRONMENT || "production"
  const { version, build } = versionConfig

  switch (env) {
    case "development":
      return `Mercury TNA v${version}-dev.${build.development || 0}`
    case "staging":
      return `Mercury TNA v${version}-stg.${build.staging || 0}`
    default:
      return `Mercury TNA v${version}`
  }
}

/**
 * Get just the version number (without Mercury TNA prefix)
 * @returns {string} Version number only
 */
export const getVersionNumber = () => {
  const env = process.env.REACT_APP_ENVIRONMENT || "production"
  const { version, build } = versionConfig

  switch (env) {
    case "development":
      return `v${version}-dev.${build.development || 0}`
    case "staging":
      return `v${version}-stg.${build.staging || 0}`
    default:
      return `v${version}`
  }
}

/**
 * Get raw version config for programmatic access
 * @returns {object} Version configuration object
 */
export const getVersionConfig = () => versionConfig

/**
 * VersionDisplay component for rendering version in UI
 * @param {object} props - Component props
 * @param {string} [props.className] - Additional CSS classes
 * @param {boolean} [props.showBrand=true] - Whether to show "Mercury TNA" prefix
 * @returns {JSX.Element}
 */
export const VersionDisplay = ({ className = "", showBrand = true }) => {
  const displayText = showBrand ? getDisplayVersion() : getVersionNumber()

  return <span className={`text-xs text-muted-foreground ${className}`}>{displayText}</span>
}

export default VersionDisplay
